/**
 * Header del frontend-superadmin — NEGRO con borde rojo (handoff
 * `PAGINAS-SUPERADMIN.md`).
 *
 * Sprint 11 v2 S11-V2-11. Diferencias con el header del portal vecino:
 * - Fondo bg-page (negro) en lugar de orange.
 * - Borde inferior admin-red (no border-tertiary).
 * - Banner secundario "SUPER ADMIN · ACCESO TÉCNICO".
 * - Sin pestañas top-level (no hay multi-rol).
 */
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';
import { Avatar } from '../ui/Avatar';

interface NavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Inicio' },
  { to: '/superadmin/lotes', label: 'Casas' },
  { to: '/superadmin/personas', label: 'Personas' },
  { to: '/superadmin/tenencias', label: 'Tenencias' },
  { to: '/superadmin/roles', label: 'Roles' },
  { to: '/superadmin/configuracion-cuota', label: 'Cuota' },
  { to: '/superadmin/pagos-iniciales', label: 'Pagos LIVE' },
  { to: '/superadmin/cargas-recursos', label: 'Recursos LIVE' },
  { to: '/superadmin/saldo-apertura', label: 'Saldo apertura' },
  { to: '/superadmin/categorias', label: 'Categorías' },
  { to: '/superadmin/proyectos', label: 'Proyectos' },
  { to: '/superadmin/entes-externos', label: 'Entes externos' },
  { to: '/superadmin/catalogo', label: 'Catálogo' },
  { to: '/superadmin/ewelink', label: 'eWeLink' },
  { to: '/configuracion-notificaciones', label: 'Notificaciones' },
];

function primerNombre(nombreCompleto: string): string {
  const t = (nombreCompleto || '').trim();
  if (!t) return '';
  const p = t.split(/\s+/)[0]!;
  return p[0]!.toUpperCase() + p.slice(1).toLowerCase();
}

export function Header() {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();

  if (!me) return null;

  async function handleLogout() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <header className="bg-bg-page border-b-2 border-admin-red">
      <div className="px-8 py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-3 shrink-0 hover:opacity-90 transition-opacity duration-base"
          aria-label="Ir al inicio"
        >
          <img
            src="/escudo-calbucura-original.png"
            alt="Escudo Calbucura"
            className="h-9 w-auto block"
          />
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-medium text-text-primary tracking-widest">
              CALBUCURA
            </span>
            <span className="text-sm text-admin-red font-medium ml-2 hidden sm:inline">
              / Super Admin
            </span>
          </div>
        </Link>

        <nav className="flex items-center gap-5 text-base text-text-secondary" aria-label="Secciones">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `transition-colors duration-base whitespace-nowrap ${
                  isActive
                    ? 'text-text-primary font-medium'
                    : 'text-text-secondary hover:text-text-primary'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          <div className="flex items-center gap-2 pl-3 border-l-[0.5px] border-white/15">
            <Avatar variant="titular" nombreCompleto={me.persona.nombre_completo} />
            <span className="text-base text-text-primary hidden sm:inline">
              {primerNombre(me.persona.nombre_completo)}
            </span>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="text-sm text-text-secondary hover:text-text-primary hover:underline underline-offset-2 ml-2"
          >
            Salir
          </button>
        </nav>
      </div>

      {/* Banner ACCESO TÉCNICO permanente */}
      <div className="bg-admin-red/10 border-t border-admin-red/30 px-8 py-2 text-center text-xs text-admin-red font-medium tracking-widest uppercase">
        ⚠ Super Admin · Acceso técnico · Cuenta de poder limitado
      </div>
    </header>
  );
}
